import { Box } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { AsyncContent } from '~/components/AsyncContent';
import { useLoggedInEmployeeId } from '~/components/Auth';
import { EmpBasicData } from '~/data/employee';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import {
  LegacyAutocompleteField,
  LegacyDateField,
  LegacyFieldReadMode,
  LegacyInputField,
  LegacyMuiButton,
  LegacyMuiTextField,
  LegacyMultiCheckbox,
} from '~/legacy-components/LegacyForms';
import { LegacyHelpDialog } from '~/legacy-components/LegacyHelpDialog';
import { LegacyHeadingWithActions } from '~/legacy-components/LegacyLayout';
import { putEmpProfileData } from '~/scenes/PeopleModule/components/Tabs/BasicTab/data';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { useHasAccessOverEmployee } from '~/services/Permissions';
import { CountryOption } from '~/types';
import { convertDateTypeDatePickerProps, getEnumName } from '~/utils/helper';

import { useEmployeeFormData } from './useEmployeeFormData';

export type IFormInput = {
  fEmpFirstName: string;
  fEmpLastName: string;
  fEmpGivenNames: string;
  fEmpGender?: string;
  fEmpDateOfBirth: Date | null;
  fEmpSocSecNum: string;
  fEmpNationality?: string;
  fEmpStatus: string;
  fEmpOrigStartDate?: Date | null;
  fEmpInfo?: string;
  fEmpPersonNumber: string;
  fEmpId: number;
  fEmpProfileId: number;
};

export const EmployeeDataForm = () => {
  const {
    empData: data,
    countryList,
    employeeStatusList,
    genderOptions,
    failedToLoad,
    isLoading,
  } = useEmployeeFormData();
  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const { hasFullAccessOverEmployee } = useHasAccessOverEmployee();
  const hasFullAccessOverEmp = hasFullAccessOverEmployee(data.fEmpId);
  const loggedInId = useLoggedInEmployeeId();

  const sortedCountryList = useMemo(() => {
    return !!countryList
      ? countryList.sort((a, b) => a.countryName.localeCompare(b.countryName))
      : [];
  }, [countryList]);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [displayInfoDialog, setDisplayInfoDialog] = useState<boolean>(false);

  const ownData = +loggedInId === +data.fEmpId;
  const empViewOwnData = ownData && !editMode;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Form validation
  const validationSchema = useMemo(() => {
    const yupStringRequired = yup.string().required(t('laFillManFields'));
    const yupDateRequired = yup
      .date()
      .required(t('laFillManFields'))
      .typeError(t('laErrorInvaDateValue'));
    const yupDateOptional = yup
      .date()
      .nullable()
      .notRequired()
      .typeError(t('laErrorInvaDateValue'));

    return yup.object().shape<IFormInput>({
      fEmpFirstName: yupStringRequired,
      fEmpLastName: yupStringRequired,
      fEmpGivenNames: yupStringRequired,
      fEmpGender: yup.string(),
      fEmpDateOfBirth: yupDateRequired,
      fEmpSocSecNum: yupStringRequired,
      fEmpNationality: yup.string(),
      fEmpStatus: yup.string(),
      fEmpOrigStartDate: yupDateOptional,
      fEmpInfo: yup.string(),
      fEmpPersonNumber: yup.string(),
      fEmpId: yup.number(),
      fEmpProfileId: yup.number(),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  // Date field value conversion functions
  const renderDate = (date: Date | unknown): string =>
    date instanceof Date ? moment(date).format('DD.MM.YYYY').toString() : '';

  const postDateFieldValue = (
    value: Date | null | undefined,
  ): string | undefined =>
    !!value ? moment(value).format('YYYY-MM-DD').toString() : undefined;

  const initDateFieldValue = (value: string | undefined): Date | null =>
    !!value ? new Date(value) : null;

  // Setup form
  const initialFieldValues: IFormInput | {} = useMemo(() => {
    if (!!data) {
      return {
        ...data,
        fEmpDateOfBirth: initDateFieldValue(data.fEmpDateOfBirth),
        fEmpOrigStartDate: initDateFieldValue(data.fEmpOrigStartDate),
      };
    }

    return {};
  }, [data]);

  const form = useForm<IFormInput>({
    mode: 'all',
    defaultValues: initialFieldValues,
    resolver: resolver as never,
  });
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = form;

  const restrictEdit =
    hasRoleAccessOverEmployee(
      [Roles.MANAGER, Roles.ALT_MANAGER, Roles.DEPUTY_MANAGER],
      employeeModulesAccessRoles.people.all,
    ) && !ownData;

  const isSelfEditDisabled =
    !hasRoleAccessOverEmployee([Roles.HR_ADMIN]) && ownData;

  const handleFormReset = () => {
    reset();
    setEditMode(false);
  };

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit(
      (formData) => {
        const postData: EmpBasicData = {
          fEmpFirstName: formData.fEmpFirstName,
          fEmpLastName: formData.fEmpLastName,
          fEmpGivenNames: formData.fEmpGivenNames,
          fEmpGender: formData.fEmpGender,
          fEmpDateOfBirth: postDateFieldValue(formData.fEmpDateOfBirth) || '',
          fEmpSocSecNum: formData.fEmpSocSecNum,
          fEmpNationality: formData.fEmpNationality,
          fEmpStatus: formData.fEmpStatus ? formData.fEmpStatus : '',
          fEmpOrigStartDate: postDateFieldValue(formData.fEmpOrigStartDate),
          fEmpInfo: formData.fEmpInfo ? formData.fEmpInfo : '',
          fEmpPersonNumber: data.fEmpPersonNumber,
          fEmpId: data.fEmpId,
          fEmpProfileId: data.fEmpProfileId,
        };

        const { fEmpFirstName, fEmpGivenNames } = formData;
        if (!fEmpGivenNames.includes(fEmpFirstName)) {
          return setDisplayInfoDialog(true);
        }

        (async () => {
          try {
            const response = await putEmpProfileData(postData, data.fEmpId);
            if (response) {
              enqueueSnackbar(t('change_saved'), {
                variant: 'success',
              });
              // setEmpData(postData);
              setEditMode(false);
            }
          } catch (error) {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          }
        })();
      },
      (errors) => {
        // TODO: Create better & generic form validation error alert/dialog/popup thing
        // console.log(errors, 'handleSubmit errors');
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [data, enqueueSnackbar, handleSubmit, t]);

  // Form field value utilities
  const getGenderName = (value: string): string => {
    return genderOptions.find((g) => g.value === value)?.label || '';
  };

  const getCountryOption = (countryCode: string): CountryOption | undefined =>
    sortedCountryList.find((c) => c.countryCode === countryCode);

  const getCountryName = (countryCode: string): string =>
    getCountryOption(countryCode)?.countryName || '';

  const getEmployeeState = (statusCode: string): string => {
    return !!employeeStatusList
      ? getEnumName(employeeStatusList, statusCode)
      : statusCode;
  };

  const loadingComplete = !isLoading && !failedToLoad;

  return (
    <>
      {displayInfoDialog && (
        <LegacyHelpDialog
          title={`${t('laInstruction')}: ${t('fEmpGivenNames')}`}
          content={<Box>{t('laGivenNameValidate')}</Box>}
          displayOnlyDialog={displayInfoDialog}
          onClose={() => setDisplayInfoDialog(false)}
        />
      )}
      <form onSubmit={(e) => e.preventDefault()}>
        <div>
          <LegacyHeadingWithActions heading={t('laPersonalData')}>
            {editMode ? (
              <>
                <LegacyMuiButton
                  onClick={handleFormSubmit}
                  data-testid="formSaveButton"
                  startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Save} />}
                >
                  {t('laSave')}
                </LegacyMuiButton>

                <LegacyMuiButton
                  onClick={handleFormReset}
                  data-testid="formCancelButton"
                  startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
                >
                  {t('laCancel')}
                </LegacyMuiButton>
              </>
            ) : (
              <>
                {!restrictEdit && (
                  <LegacyMuiButton
                    data-testid="editButton"
                    onClick={() => setEditMode(true)}
                    startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Edit} />}
                    disabled={!loadingComplete}
                  >
                    {t('laEdit')}
                  </LegacyMuiButton>
                )}
              </>
            )}
          </LegacyHeadingWithActions>
        </div>
        <AsyncContent
          isLoading={isLoading}
          failedToLoad={failedToLoad}
          failedToLoadMessage={t('laFailedToLoad')}
        >
          <div>
            <Controller
              name="fEmpFirstName"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const label = t('fEmpFirstName');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <LegacyInputField
                    fullWidth
                    name={'fEmpFirstName'}
                    value={value}
                    label={label}
                    onChange={onChange}
                    fieldError={errors.fEmpFirstName}
                    required={true}
                    data-testId="firstName"
                  />
                );
              }}
            />
            <Controller
              name="fEmpGivenNames"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const label = t('fEmpGivenNames');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <LegacyInputField
                    fullWidth
                    name={'fEmpGivenNames'}
                    value={value}
                    label={label}
                    onChange={onChange}
                    fieldError={errors.fEmpGivenNames}
                    required={true}
                    data-testid="givenName"
                    help={{
                      content: `${t('laGivenNameValidate')}`,
                      title: `${t('laInstruction')}: ${t('fEmpGivenNames')}`,
                    }}
                  />
                );
              }}
            />
            <Controller
              name="fEmpLastName"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const label = t('fEmpLastName');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <LegacyInputField
                    fullWidth
                    name={'fEmpLastName'}
                    value={value}
                    label={label}
                    onChange={onChange}
                    fieldError={errors.fEmpLastName}
                    required={true}
                    data-testId="lastName"
                  />
                );
              }}
            />
            <Controller
              name="fEmpGender"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const label = t('fEmpGender');
                if (!editMode) {
                  return (
                    <LegacyFieldReadMode
                      label={label}
                      value={getGenderName(value || '')}
                      data-testId="genderField"
                    />
                  );
                }

                return (
                  <LegacyMultiCheckbox
                    fieldName={'fEmpGender'}
                    value={value as string}
                    onChange={onChange}
                    options={genderOptions}
                    label={label}
                    fieldError={errors.fEmpGender}
                  />
                );
              }}
            />
            {(hasFullAccessOverEmp || empViewOwnData) && (
              <>
                <Controller
                  name="fEmpDateOfBirth"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpDateOfBirth');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={renderDate(value)}
                        />
                      );
                    }

                    return (
                      <LegacyDateField
                        label={label}
                        {...convertDateTypeDatePickerProps({
                          value,
                          onChange,
                        })}
                        disabled={isSelfEditDisabled}
                        fieldError={errors.fEmpDateOfBirth}
                        required={true}
                        data-testId="dateOfBirth"
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpSocSecNum"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpSocSecNum');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode label={label} value={value} />
                      );
                    }

                    return (
                      <LegacyInputField
                        fullWidth
                        name={'fEmpSocSecNum'}
                        value={value}
                        label={label}
                        disabled={isSelfEditDisabled}
                        onChange={onChange}
                        fieldError={errors.fEmpSocSecNum}
                        required={true}
                        data-testid="socialSecurityNumber"
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpNationality"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpNationality');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={getCountryName(value || '')}
                        />
                      );
                    }

                    return (
                      <LegacyAutocompleteField
                        label={label}
                        options={sortedCountryList}
                        getOptionLabel={(c) => c.countryName}
                        isOptionEqualToValue={(o, v) =>
                          o.countryCode === v.countryCode
                        }
                        onChange={(e, value) => {
                          onChange(value?.countryCode || '');
                        }}
                        disabled={isSelfEditDisabled}
                        value={getCountryOption(value || '') || null}
                        fieldError={errors.fEmpNationality}
                        data-testId="nationalityField"
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpStatus"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpStatus');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={getEmployeeState(value)}
                        />
                      );
                    }

                    return (
                      <LegacyMuiTextField
                        label={label}
                        value={value}
                        disabled={isSelfEditDisabled}
                        onChange={onChange}
                        hidden
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpOrigStartDate"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, value } }) => {
                    const label = t('laEmpOrigStartDate');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={renderDate(value)}
                        />
                      );
                    }

                    return (
                      <LegacyDateField
                        label={label}
                        {...convertDateTypeDatePickerProps({
                          value,
                          onChange,
                        })}
                        disabled={isSelfEditDisabled}
                        fieldError={errors.fEmpOrigStartDate}
                        data-testid="originalStartDate"
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpInfo"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpInfo');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={value || ''}
                        />
                      );
                    }

                    return (
                      <>
                        <LegacyInputField
                          fullWidth
                          name={'fEmpInfo'}
                          value={value}
                          label={label}
                          disabled={isSelfEditDisabled}
                          onChange={onChange}
                          fieldError={errors.fEmpInfo}
                          multiline={true}
                          minRows={3}
                          data-testId="infoField"
                        />
                      </>
                    );
                  }}
                />
              </>
            )}
          </div>
        </AsyncContent>
      </form>
    </>
  );
};
